/**
 * Linear Regression Channel
 *
 * Draws a linear regression line through prices and upper/lower
 * channel bands at ±N standard deviations.
 *
 * Category: Trend | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { getSource } from "./sma";

export const lrChannelIndicator: IndicatorDefinition = {
  id: "lr_channel",
  name: "Linear Regression Channel",
  shortName: "LRCh",
  category: "trend",
  pane: "overlay",
  params: [
    { key: "period", label: "Period", type: "number", default: 100, min: 10, max: 500 },
    { key: "deviations", label: "Deviations", type: "number", default: 2.0, min: 0.5, max: 5.0, step: 0.1 },
    { key: "source", label: "Source", type: "select", default: "close", options: [
      { label: "Close", value: "close" }, { label: "HL2", value: "hl2" },
      { label: "HLC3", value: "hlc3" }, { label: "OHLC4", value: "ohlc4" },
    ] },
  ],
  plots: [
    { key: "mid", label: "Mid", color: "#2196F3", style: "line", lineWidth: 2 },
    { key: "upper", label: "Upper", color: "#F44336", style: "line", lineWidth: 1 },
    { key: "lower", label: "Lower", color: "#4CAF50", style: "line", lineWidth: 1 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period, deviations, source } = params;
    const src = getSource(candles, source);
    const midData: { time: number; value: number }[] = [];
    const upperData: { time: number; value: number }[] = [];
    const lowerData: { time: number; value: number }[] = [];

    for (let i = period - 1; i < candles.length; i++) {
      // Compute linear regression over window
      let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
      for (let j = 0; j < period; j++) {
        const y = src[i - period + 1 + j];
        sumX += j;
        sumY += y;
        sumXY += j * y;
        sumX2 += j * j;
      }

      const n = period;
      const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
      const intercept = (sumY - slope * sumX) / n;
      const regValue = intercept + slope * (period - 1);

      // Standard deviation around the regression line
      let sumSq = 0;
      for (let j = 0; j < period; j++) {
        const expected = intercept + slope * j;
        const diff = src[i - period + 1 + j] - expected;
        sumSq += diff * diff;
      }
      const stdDev = Math.sqrt(sumSq / period);

      midData.push({ time: candles[i].time, value: regValue });
      upperData.push({ time: candles[i].time, value: regValue + deviations * stdDev });
      lowerData.push({ time: candles[i].time, value: regValue - deviations * stdDev });
    }

    return [
      { key: "mid", data: midData },
      { key: "upper", data: upperData },
      { key: "lower", data: lowerData },
    ];
  },
};
